import {
	createTheme,
	CSSVariablesResolver,
	MantineColorsTuple,
} from "@mantine/core";

// "Redesign portail Hooky" mockup tokens: primary #3d7df5 (shade 6),
// hover/active #2b64d8 (shade 7); the rest of the ramp interpolates.
const hooky: MantineColorsTuple = [
	"#e9f1fe",
	"#d5e4fd",
	"#b0cdfb",
	"#8bb5f9",
	"#6ea2f8",
	"#558ff6",
	"#3d7df5",
	"#2b64d8",
	"#2354b6",
	"#1b4494",
];

// Light-mode ramp: body #eef2f8, panels #f2f5fa/#e8edf4, borders ~#dde4ee,
// muted #8195ad/#4d627e, headings #14243a.
const gray: MantineColorsTuple = [
	"#f8fafd",
	"#f2f5fa",
	"#e8edf4",
	"#dde4ee",
	"#c6d1e0",
	"#a3b2c7",
	"#8195ad",
	"#4d627e",
	"#2c4059",
	"#14243a",
];

// Dark-mode surfaces: body #0a1522 (7), cards/inputs #0f1d2e (6),
// hover #16293f (5), borders #24384f (4), text #eef4fb/#93a6c0/#647a97 (0-2).
const dark: MantineColorsTuple = [
	"#eef4fb",
	"#93a6c0",
	"#647a97",
	"#3a5273",
	"#24384f",
	"#16293f",
	"#0f1d2e",
	"#0a1522",
	"#081020",
	"#050b14",
];

// Accent ramps centered on the mockup hexes at shade 6 — flat tuples break
// the light variant (shade 0 becomes the background in light mode).
const success: MantineColorsTuple = [
	"#e6f7f0",
	"#d0f0e3",
	"#a8e3cb",
	"#7fd5b3",
	"#5cc79e",
	"#4bc092",
	"#3fb98a",
	"#2a9469",
	"#217753",
	"#185a3f",
];

const info: MantineColorsTuple = [
	"#e3f6fb",
	"#cdeff8",
	"#a0e1f1",
	"#72d2ea",
	"#4cc5e3",
	"#38bdde",
	"#2bb6da",
	"#1789b0",
	"#126e8e",
	"#0d536b",
];

const warning: MantineColorsTuple = [
	"#fdf2e0",
	"#fbe7c6",
	"#f8d494",
	"#f4c061",
	"#f1ad37",
	"#f0a220",
	"#ef9a12",
	"#c9760a",
	"#a35f08",
	"#7c4906",
];

const danger: MantineColorsTuple = [
	"#fceef1",
	"#f9dce2",
	"#f3b8c4",
	"#ec94a5",
	"#e7748b",
	"#e6687f",
	"#e5607a",
	"#c53e5c",
	"#a1324b",
	"#7c263a",
];

const secondary: MantineColorsTuple = [
	"#eef1f4",
	"#dde3e9",
	"#bcc8d2",
	"#9aacbb",
	"#7d92a5",
	"#6c8194",
	"#5f7488",
	"#42566a",
	"#364756",
	"#2a3743",
];

export const hookyTheme = createTheme({
	colors: {
		hooky,
		gray,
		dark,
		success,
		info,
		warning,
		danger,
		secondary,
	},
	primaryColor: "hooky",
	primaryShade: 6,
	autoContrast: true,
	luminanceThreshold: 0.45,
	defaultGradient: { from: "#4a8bff", to: "#2b64d8", deg: 180 },

	fontFamily: "Figtree, system-ui, sans-serif",
	// Space Grotesk carries every numeric readout (durations, BPM, metrics,
	// UUIDs) — reach it with ff="monospace" on Text/Table cells.
	fontFamilyMonospace: "'Space Grotesk', system-ui, sans-serif",
	// Mockup scale: chips 10-11, muted 12-13, body 13.5-14, emphasis 15.
	fontSizes: {
		xs: "0.75rem",
		sm: "0.8125rem",
		md: "0.875rem",
		lg: "0.9375rem",
		xl: "1.0625rem",
	},
	lineHeights: {
		md: "1.55",
	},
	headings: {
		fontFamily: "Figtree, system-ui, sans-serif",
		fontWeight: "700",
		sizes: {
			h1: { fontSize: "1.5625rem", lineHeight: "1.2" },
			h2: { fontSize: "1.0625rem", lineHeight: "1.25" },
			h3: { fontSize: "1rem", lineHeight: "1.25" },
			h4: { fontSize: "0.9375rem", lineHeight: "1.25" },
			h5: { fontSize: "0.875rem", lineHeight: "1.25" },
			h6: { fontSize: "0.8125rem", lineHeight: "1.25" },
		},
	},

	// Mockup radii: chips 6, buttons/inputs ~10, cards 14, panels/modals 16.
	radius: {
		xs: "6px",
		sm: "8px",
		md: "10px",
		lg: "14px",
		xl: "16px",
	},
	defaultRadius: "md",
	shadows: {
		sm: "0 2px 6px rgba(5, 9, 15, 0.25)",
		md: "0 14px 30px -16px rgba(5, 9, 15, 0.45)",
		lg: "0 40px 90px -30px rgba(5, 9, 15, 0.6)",
	},

	components: {
		Title: {
			styles: {
				root: { letterSpacing: "-0.02em" },
			},
		},
		Button: {
			defaultProps: { radius: "md", size: "sm" },
			styles: {
				root: { fontWeight: 600 },
			},
		},
		ActionIcon: {
			defaultProps: { variant: "subtle", color: "gray", radius: "md" },
		},
		Card: {
			defaultProps: { radius: "lg", padding: "lg", withBorder: true },
		},
		Paper: {
			defaultProps: { radius: "lg" },
		},
		Badge: {
			defaultProps: {
				radius: "xs",
				variant: "filled",
				size: "sm",
			},
			styles: {
				root: {
					fontSize: "0.65rem",
					fontWeight: 600,
					letterSpacing: "0.05em",
				},
			},
		},
		Modal: {
			defaultProps: {
				radius: "xl",
				centered: true,
				padding: "lg",
				overlayProps: { backgroundOpacity: 0.6, blur: 3 },
				transitionProps: { transition: "pop", duration: 200 },
			},
			styles: {
				content: {
					backgroundColor: "var(--hooky-surface)",
					border: "1px solid var(--hooky-border-strong)",
				},
				header: {
					backgroundColor: "var(--hooky-surface)",
				},
			},
		},
		Tooltip: {
			defaultProps: { radius: "md", withArrow: true },
		},
		Menu: {
			// hideDetached off app-wide: Floating UI would hide portaled
			// dropdowns whose reference is considered clipped (tables in
			// ScrollContainers, 68vh lists), and jsdom's zero-sized rects
			// make the heuristic misfire in tests.
			defaultProps: {
				radius: "lg",
				shadow: "md",
				offset: 4,
				hideDetached: false,
			},
			styles: {
				item: {
					padding: "0.625rem 1.25rem",
					fontSize: "12px",
					fontWeight: 500,
				},
			},
		},
		Popover: {
			defaultProps: { hideDetached: false },
		},
		Combobox: {
			defaultProps: { hideDetached: false },
		},
		NavLink: {
			styles: {
				root: {
					fontWeight: 500,
					borderRadius: "var(--mantine-radius-md)",
				},
			},
		},
		Tabs: {
			styles: {
				tab: {
					fontWeight: 600,
					fontSize: "var(--mantine-font-size-md)",
				},
			},
		},
		TextInput: {
			defaultProps: { size: "sm", radius: "md" },
		},
		PasswordInput: {
			defaultProps: { size: "sm", radius: "md" },
		},
		Select: {
			defaultProps: { size: "sm", radius: "md" },
		},
		MultiSelect: {
			defaultProps: { size: "sm", radius: "md" },
		},
		Textarea: {
			defaultProps: { size: "sm", radius: "md" },
		},
		NumberInput: {
			defaultProps: { size: "sm", radius: "md" },
		},
		Checkbox: {
			defaultProps: { radius: "xs", size: "sm" },
		},
		Table: {
			defaultProps: { highlightOnHover: true, verticalSpacing: "sm" },
			// Mockup .tbl panel: surface background, hairline border, rounded
			// corners (overflow hidden clips the thead band), panel2 header row.
			styles: {
				table: {
					"backgroundColor": "var(--hooky-surface)",
					"border": "1px solid var(--hooky-border)",
					"borderRadius": "var(--mantine-radius-lg)",
					"overflow": "hidden" as const,
					"--table-highlight-on-hover-color":
						"var(--hooky-surface-strong)",
				},
				th: {
					fontSize: "0.6875rem",
					fontWeight: 600,
					letterSpacing: "0.06em",
					textTransform: "uppercase" as const,
					color: "var(--hooky-muted-2)",
					backgroundColor: "var(--hooky-surface-strong)",
				},
				td: {
					fontSize: "0.84375rem",
				},
			},
		},
		Pagination: {
			defaultProps: { radius: "sm", size: "md" },
		},
		SegmentedControl: {
			defaultProps: { radius: "md" },
		},
		Progress: {
			defaultProps: { radius: "sm", size: "md", color: "hooky" },
		},
		Slider: {
			defaultProps: { color: "hooky", size: "sm" },
		},
		Loader: {
			defaultProps: { type: "oval", color: "hooky" },
		},
	},
});

export const hookyCssVariablesResolver: CSSVariablesResolver = () => ({
	variables: {},
	light: {
		"--mantine-color-body": "#eef2f8",
		"--mantine-color-text": "#14243a",
		"--hooky-sidebar": "#ffffff",
		"--hooky-surface": "#ffffff",
		"--hooky-surface-strong": "#f2f5fa",
		"--hooky-surface-deep": "#e8edf4",
		"--hooky-border": "rgba(12, 28, 50, 0.09)",
		"--hooky-border-strong": "rgba(12, 28, 50, 0.16)",
		"--hooky-muted": "#4d627e",
		"--hooky-muted-2": "#8195ad",
		"--hooky-heading": "#14243a",
	},
	dark: {
		"--mantine-color-body": "#0a1522",
		"--mantine-color-text": "#eef4fb",
		"--hooky-sidebar": "#0c1a2b",
		"--hooky-surface": "#0f1d2e",
		"--hooky-surface-strong": "#12233a",
		"--hooky-surface-deep": "#16293f",
		"--hooky-border": "rgba(255, 255, 255, 0.08)",
		"--hooky-border-strong": "rgba(255, 255, 255, 0.14)",
		"--hooky-muted": "#93a6c0",
		"--hooky-muted-2": "#647a97",
		"--hooky-heading": "#eef4fb",
	},
});
